import { Constants } from "./constants";

/**
 * UKC Rally Obedience classes.
 *
 * Canonical storage is `dog_class.level` = RO1 / RO2 / RO3 / ROM with
 * `dog_class.section` = A / B / C. Older shows and scores carry the single
 * string form `level: "RO1 B"` with no section. Everything that compares,
 * groups or looks up a UKC Rally class should go through these helpers so
 * both forms are treated as the same class.
 *
 * URX and RMX are multi-entry levels with no section and pass through unchanged.
 */

export interface IUkcRallyClass {
  level: string;
  section: string;
}

const L = Constants.dog_class_element_level;
const S = Constants.dog_class_section;

/** Canonical levels in running order. */
export const UKC_RALLY_LEVELS: string[] = [L.ro1, L.ro2, L.ro3, L.rom];

/** Sections each canonical level can offer. */
export const UKC_RALLY_SECTIONS: Record<string, string[]> = {
  [L.ro1]: [S.ukc_rally_a, S.ukc_rally_b, S.ukc_rally_c],
  [L.ro2]: [S.ukc_rally_a, S.ukc_rally_b],
  [L.ro3]: [S.ukc_rally_a, S.ukc_rally_b],
  [L.rom]: [S.ukc_rally_a, S.ukc_rally_b],
};

/** Every level + section pair in running order, as legacy "RO1 A" labels. */
export const UKC_RALLY_LEGACY_LEVELS: string[] = UKC_RALLY_LEVELS.flatMap((level) =>
  UKC_RALLY_SECTIONS[level].map((section) => `${level} ${section}`)
);

const LEGACY_PATTERN = /^(RO1|RO2|RO3|ROM)\s+([ABC])$/i;

export function isUkcRallyShow(sanctioningClub?: string, showType?: string): boolean {
  return sanctioningClub === Constants.sanctioning_club.UKC && showType === Constants.show_type.rally_obedience;
}

/** True for RO1/RO2/RO3/ROM in either form. URX/RMX return false. */
export function isUkcRallyLevel(level?: string): boolean {
  if (!level) return false;
  const trimmed = level.trim();
  return UKC_RALLY_LEVELS.includes(trimmed.toUpperCase()) || LEGACY_PATTERN.test(trimmed);
}

/**
 * Splits either form into level + section.
 * ("RO1 B", undefined) -> { RO1, B }
 * ("RO1", "B")         -> { RO1, B }
 * ("RO1 B", "B")       -> { RO1, B }
 * ("URX, UROC & UROG") -> { URX, UROC & UROG, "" }
 */
export function parseUkcRallyClass(level?: string, section?: string | null): IUkcRallyClass {
  const rawLevel = (level || "").trim();
  const rawSection = (section || "").trim().toUpperCase();
  const legacy = rawLevel.match(LEGACY_PATTERN);
  if (legacy) {
    // Legacy rows carry a defaulted section ("A") that has nothing to do with the class; the label is the truth.
    return { level: legacy[1].toUpperCase(), section: legacy[2].toUpperCase() };
  }
  const upper = rawLevel.toUpperCase();
  if (UKC_RALLY_LEVELS.includes(upper)) {
    return { level: upper, section: rawSection };
  }
  return { level: rawLevel, section: rawSection };
}

/** "RO1 B" for a sectioned class, the level alone otherwise. Matches the legacy string. */
export function ukcRallyClassLabel(level?: string, section?: string | null): string {
  const parsed = parseUkcRallyClass(level, section);
  return parsed.section ? `${parsed.level} ${parsed.section}` : parsed.level;
}

/** Stable key for grouping and lookups, independent of storage form. */
export function ukcRallyClassKey(level?: string, section?: string | null): string {
  const parsed = parseUkcRallyClass(level, section);
  return `${parsed.level}|${parsed.section}`;
}

export function isSameUkcRallyClass(
  a: { level?: string; section?: string | null },
  b: { level?: string; section?: string | null }
): boolean {
  return ukcRallyClassKey(a.level, a.section) === ukcRallyClassKey(b.level, b.section);
}

/**
 * Index into the per-level tables (deductions, running times, sign hierarchy).
 * Legacy "RO1 B" and canonical "RO1" both resolve to "RO1"; URX/RMX pass through.
 */
export function ukcRallyTableLevel(level?: string): string {
  return parseUkcRallyClass(level).level;
}

/**
 * Levels whose signs a course at `level` may use (its own plus every lower level).
 * Keyed by canonical level; the A/B/C sections of a level share one sign pool.
 */
export const UKC_RALLY_SIGN_LEVELS: Record<string, string[]> = {
  [L.ro1]: [L.ro1],
  [L.ro2]: [L.ro1, L.ro2],
  [L.ro3]: [L.ro1, L.ro2, L.ro3],
  [L.rom]: [L.ro1, L.ro2, L.ro3, L.rom],
};

/** Sign-pool levels for either storage form of `level`; unknown levels return themselves. */
export function ukcRallySignLevels(level?: string): string[] {
  const tableLevel = ukcRallyTableLevel(level);
  return UKC_RALLY_SIGN_LEVELS[tableLevel] || [level || ""];
}

/**
 * True when `candidate` falls under `filter`. A filter with a level and no section
 * matches every section of that level; a filter with a section matches that one class.
 * Non-rally levels compare by plain equality.
 */
export function ukcRallyClassMatches(
  candidate: { level?: string; section?: string | null },
  filter: { level?: string; section?: string | null }
): boolean {
  const c = parseUkcRallyClass(candidate.level, candidate.section);
  const f = parseUkcRallyClass(filter.level, filter.section);
  if (c.level !== f.level) return false;
  return !f.section || c.section === f.section;
}

/**
 * Mongo match for a UKC Rally class in either storage form, for use inside a
 * scores / registrations query. Returns null for levels that are not RO1..ROM
 * (URX, RMX and every other sport) so callers fall back to a plain equality filter.
 *
 * ("RO1", "B")  -> { $or: [ { level: "RO1 B" }, { level: "RO1", section: "B" } ] }
 * ("RO1", null) -> { $or: [ { level: { $in: ["RO1 A", "RO1 B", "RO1 C"] } }, { level: "RO1" } ] }
 */
export function ukcRallyDogClassMatch(
  level?: string,
  section?: string | null,
  fieldPrefix = "dog_class."
): { $or: Record<string, any>[] } | null {
  if (!isUkcRallyLevel(level)) return null;
  const parsed = parseUkcRallyClass(level, section);
  const levelField = `${fieldPrefix}level`;
  const sectionField = `${fieldPrefix}section`;
  if (parsed.section) {
    return {
      $or: [
        { [levelField]: `${parsed.level} ${parsed.section}` },
        { [levelField]: parsed.level, [sectionField]: parsed.section },
      ],
    };
  }
  const legacyLevels = (UKC_RALLY_SECTIONS[parsed.level] || []).map((s) => `${parsed.level} ${s}`);
  return {
    $or: [
      { [levelField]: { $in: legacyLevels } },
      { [levelField]: parsed.level },
    ],
  };
}

/**
 * Sections a level can offer. A legacy label ("RO1 B") already carries its section and
 * returns none; URX/RMX and unknown levels have none.
 */
export function ukcRallyLevelSections(level?: string): string[] {
  const parsed = parseUkcRallyClass(level);
  if (parsed.section) return [];
  return UKC_RALLY_SECTIONS[parsed.level] || [];
}

/**
 * How a show stores its UKC Rally classes.
 * "legacy"    - show_elements carry level "RO1 B" with no section (shows created before the refactor)
 * "canonical" - show_elements carry level "RO1" + section "B"
 * A show with no sectioned rally element yet (new show, or URX/RMX only) is canonical.
 */
export type UkcRallyClassFormat = "legacy" | "canonical";

export function ukcRallyShowFormat(
  showElements?: { level?: string; section?: string | null }[] | null
): UkcRallyClassFormat {
  const hasLegacy = (showElements || []).some(
    (se) => !!se.level && LEGACY_PATTERN.test(se.level.trim())
  );
  return hasLegacy ? "legacy" : "canonical";
}

/**
 * Rewrites a class into the given storage format. Only RO1..ROM are touched; URX/RMX and
 * other sports pass through unchanged. Extra properties on the class are preserved.
 */
export function toUkcRallyFormat<T extends { level?: string; section?: string | null }>(
  dogClass: T,
  format: UkcRallyClassFormat
): T {
  if (!isUkcRallyLevel(dogClass.level)) return dogClass;
  const parsed = parseUkcRallyClass(dogClass.level, dogClass.section);
  if (!parsed.section) return dogClass;
  if (format === "legacy") {
    const { section: _section, ...rest } = dogClass as T & { section?: string | null };
    return { ...rest, level: `${parsed.level} ${parsed.section}` } as T;
  }
  return { ...dogClass, level: parsed.level, section: parsed.section };
}

/** Sort position across all level + section pairs; unknown classes sort last. */
export function ukcRallyClassOrder(level?: string, section?: string | null): number {
  const index = UKC_RALLY_LEGACY_LEVELS.indexOf(ukcRallyClassLabel(level, section));
  return index === -1 ? UKC_RALLY_LEGACY_LEVELS.length : index;
}
